/**
 * Small building blocks shared by the shell and the editor: a modal `Dialog`, `ConfirmDialog`,
 * `PromptDialog` (one text field), and toasts (`toast("Plan copied")` + one `<Toaster/>` in App).
 */
import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from 'react';
import { ApiFailure } from '../../api';

interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Wider dialog (e.g. previews). */
  wide?: boolean;
}

/** A modal dialog; Escape or clicking outside closes it. */
export function Dialog({ title, onClose, children, wide }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[autofocus], input, button.primary, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

interface ConfirmProps {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

/** "Are you sure?" with a main button and Cancel. Shows a friendly error if the action fails. */
export function ConfirmDialog({ title, children, confirmLabel, danger, onConfirm, onClose }: ConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog title={title} onClose={onClose}>
      <div className="dialog-body">{children}</div>
      {error && <p className="error">{error}</p>}
      <div className="dialog-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className={danger ? 'danger' : 'primary'} disabled={busy} onClick={go}>
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

interface PromptProps {
  title: string;
  label: string;
  initial?: string;
  placeholder?: string;
  confirmLabel: string;
  /** Allow an empty answer (e.g. an unnamed version). */
  allowEmpty?: boolean;
  maxLength?: number;
  onSubmit: (value: string) => void | Promise<void>;
  onClose: () => void;
}

/** A dialog with one text field. */
export function PromptDialog({ title, label, initial = '', placeholder, confirmLabel, allowEmpty, maxLength = 80, onSubmit, onClose }: PromptProps) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!allowEmpty && !value.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onSubmit(value.trim());
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <Dialog title={title} onClose={onClose}>
      <form onSubmit={submit}>
        <label className="field">
          <span>{label}</span>
          <input
            autoFocus
            value={value}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary" disabled={busy || (!allowEmpty && !value.trim())}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ---- Toasts ----------------------------------------------------------------------------------

interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error';
}

let toasts: Toast[] = [];
let nextToast = 1;
const toastListeners = new Set<() => void>();
const emitToasts = () => toastListeners.forEach((l) => l());

/** Shows a short message at the bottom of the screen for a few seconds. */
export function toast(text: string, kind: 'info' | 'error' = 'info'): void {
  const t = { id: nextToast++, text, kind };
  toasts = [...toasts, t];
  emitToasts();
  setTimeout(() => {
    toasts = toasts.filter((x) => x.id !== t.id);
    emitToasts();
  }, kind === 'error' ? 6000 : 3500);
}

/** Renders the toasts; put one in the App. */
export function Toaster() {
  const list = useSyncExternalStore(
    (l) => {
      toastListeners.add(l);
      return () => toastListeners.delete(l);
    },
    () => toasts,
  );
  return (
    <div className="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`}>{t.text}</div>
      ))}
    </div>
  );
}

/** The message of an error, for showing to the user (API messages are friendly; others are not shown). */
export function errorText(e: unknown): string {
  return e instanceof ApiFailure ? e.message : 'Something went wrong. Please try again.';
}
